import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';

import { countryName } from '@/features/markets';

import { hasRegions, regionsOf } from '../schemas/regions';

export interface RegionPickerProps {
  /** Countries in the zone; only those with a known region table get a block. */
  countries: readonly string[];
  value: Record<string, string[]>;
  onChange: (value: Record<string, string[]>) => void;
  disabled?: boolean;
}

/**
 * Per country: the whole country (default) or a hand-picked set of states or provinces. A
 * country without a table is always shipped whole, so it gets no block.
 */
export function RegionPicker({ countries, value, onChange, disabled }: RegionPickerProps) {
  const { t } = useTranslation('shipping');
  const withRegions = countries.filter(hasRegions);
  if (withRegions.length === 0) return null;

  const setCountry = (country: string, codes: string[] | null) => {
    const kept = Object.entries(value).filter(([c]) => c !== country);
    onChange(Object.fromEntries(codes === null ? kept : [...kept, [country, codes]]));
  };

  return (
    <div className="flex flex-col gap-4">
      {withRegions.map((country) => {
        const picked = value[country];
        const partial = picked !== undefined;
        const regions = regionsOf(country);
        const groupId = `regions-${country}`;
        return (
          <fieldset key={country} className="rounded-md border p-3" disabled={disabled}>
            <legend className="px-1 text-sm font-medium">{countryName(country)}</legend>
            <RadioGroup
              value={partial ? 'regions' : 'whole'}
              onValueChange={(mode) => setCountry(country, mode === 'whole' ? null : [])}
              aria-label={t('zone.regions.modeLabel', { country: countryName(country) })}
              className="flex flex-wrap gap-4"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="whole" id={`${groupId}-whole`} />
                <Label htmlFor={`${groupId}-whole`} className="font-normal">
                  {t('zone.regions.whole')}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="regions" id={`${groupId}-some`} />
                <Label htmlFor={`${groupId}-some`} className="font-normal">
                  {t('zone.regions.some')}
                </Label>
              </div>
            </RadioGroup>
            {partial ? (
              <div className="mt-3 flex flex-col gap-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground text-xs">
                    {t('zone.regions.picked', { count: picked.length, total: regions.length })}
                  </span>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setCountry(
                          country,
                          regions.map((r) => r.code),
                        )
                      }
                    >
                      {t('zone.regions.selectAll')}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setCountry(country, [])}
                    >
                      {t('zone.regions.clear')}
                    </Button>
                  </div>
                </div>
                <ul
                  className="grid max-h-48 gap-1.5 overflow-y-auto pr-1 sm:grid-cols-2"
                  aria-label={t('zone.regions.listLabel', { country: countryName(country) })}
                >
                  {regions.map((region) => {
                    const id = `${groupId}-${region.code}`;
                    const checked = picked.includes(region.code);
                    return (
                      <li key={region.code} className="flex items-center gap-2">
                        <Checkbox
                          id={id}
                          checked={checked}
                          onCheckedChange={(next) =>
                            setCountry(
                              country,
                              next === true
                                ? [...picked, region.code]
                                : picked.filter((c) => c !== region.code),
                            )
                          }
                        />
                        <Label htmlFor={id} className="truncate font-normal">
                          {region.name}
                          <span className="text-muted-foreground ml-1 font-mono text-[11px]">
                            {region.code}
                          </span>
                        </Label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </fieldset>
        );
      })}
    </div>
  );
}
